import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DragHandle, EditActions } from './EditControls';
import { labelSx } from './templates/mobileText';

const chipSx = (tone) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  height: 22,
  borderRadius: '11px',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  bgcolor: tone === 'amber' ? '#fef3e2' : tone === 'accent' ? 'rgba(49,130,206,0.10)' : tone === 'red' ? '#fee2e2' : '#f8fafc',
  color: tone === 'amber' ? '#92400e' : tone === 'accent' ? '#3182ce' : tone === 'red' ? '#c53030' : '#64748b',
  border: tone ? '1px solid transparent' : '1px solid #e2e8f0',
});

/**
 * Widget header (design: 48 px, title + chip + link + settings; edit mode swaps in the drag
 * handle, size toggle and remove). Content comes from the frame context (`setHeader`).
 */
export default function WidgetHeader({ frame, header, stale, ageMinutes }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, mode } = frame;
  const title = header.title || frame.title;
  const edit = mode === 'edit';
  const link = header.link;
  // Phone header: the title comes first and may take two lines, the chip wraps under it when
  // the row is full, and the link shrinks to a chevron so "Avaa raportit" cannot eat the title.
  const mobile = Boolean(frame.isMobile) && !edit;
  const linkLabel = link ? link.label || t(link.labelKey) : null;

  const titleNode = (
    <Typography
      component="span"
      data-widget-title=""
      sx={{ fontSize: 14, fontWeight: 600, color: header.titleTone === 'red' ? '#c53030' : 'text.primary', minWidth: 0, ...labelSx(mobile) }}
    >
      {title}
      {header.count != null ? ` · ${header.count}` : ''}
    </Typography>
  );

  return (
    <Box
      sx={{
        height: mobile ? 'auto' : 48,
        minHeight: 48,
        py: mobile ? 1 : 0,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: edit ? '6px 10px' : 2.25,
        pl: edit ? 0.75 : 2.25,
        pr: edit ? 1.25 : 1.5,
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      {edit ? (
        <>
          <DragHandle frame={frame}>{titleNode}</DragHandle>
          <EditActions frame={frame} />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, rowGap: 0.5, flex: 1, minWidth: 0, flexWrap: mobile ? 'wrap' : 'nowrap' }}>
            {titleNode}
            {stale ? (
              <Tooltip title={t('dashboard.state.updatedAgo', { min: ageMinutes })}>
                <Box component="span" data-testid={`widget-${id}-stale`} sx={chipSx('amber')}>
                  <ScheduleIcon sx={{ fontSize: 14 }} />
                  {t('dashboard.state.staleMinutes', { min: ageMinutes })}
                </Box>
              </Tooltip>
            ) : header.chip ? (
              <Box component="span" sx={chipSx(header.chipTone)}>
                {header.chip}
              </Box>
            ) : null}
          </Box>
          {link && mobile ? (
            <IconButton
              size="small"
              data-testid={`widget-${id}-link`}
              aria-label={linkLabel}
              onClick={() => (link.onClick ? link.onClick() : navigate(link.to))}
              sx={{ color: 'secondary.main', width: 28, height: 28 }}
            >
              <ChevronRightIcon sx={{ fontSize: 22 }} />
            </IconButton>
          ) : link ? (
            <Typography
              component="button"
              type="button"
              data-testid={`widget-${id}-link`}
              onClick={() => (link.onClick ? link.onClick() : navigate(link.to))}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                background: 'none',
                border: 0,
                p: 0,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                color: 'secondary.main',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {linkLabel}
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </Typography>
          ) : null}
          {frame.entry.settings?.length ? (
            <IconButton
              size="small"
              data-testid={`widget-${id}-settings`}
              aria-label={t('dashboard.edit.settings')}
              onClick={(event) => frame.onOpenSettings(event.currentTarget)}
              sx={{ color: '#64748b', width: 28, height: 28 }}
            >
              <MoreHorizIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : null}
        </>
      )}
    </Box>
  );
}
